import { useAppStore } from '@/lib/store';
import { formatNumber } from '@/lib/utils';
import {
  Wifi,
  Zap,
  DollarSign,
  Activity,
  Globe,
  RefreshCw,
} from 'lucide-react';

export default function ChainStatus() {
  const { energyUnitSun, bandwidthUnitSun, trxPrice, fetchTrxPrice } =
    useAppStore();

  const isConnected =
    energyUnitSun !== undefined && bandwidthUnitSun !== undefined;

  return (
    <div className="space-y-6">
      {/* Network Status */}
      <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white flex items-center space-x-2">
            <Globe className="h-5 w-5 text-blue-400" />
            <span>TRON Network Status</span>
          </h3>
          <div className="flex items-center space-x-2">
            <div
              className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-400' : 'bg-red-400'}`}
            ></div>
            <span
              className={`text-sm ${isConnected ? 'text-green-400' : 'text-red-400'}`}
            >
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Energy Unit Price */}
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-yellow-400" />
                <span className="text-sm font-medium text-slate-300">
                  Energy Unit Price
                </span>
              </div>
              <span className="text-xs text-slate-500">SUN per unit</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {isConnected ? formatNumber(energyUnitSun) : '--'}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Cost per Energy unit in SUN (1 TRX = 1,000,000 SUN)
            </p>
          </div>

          {/* Bandwidth Unit Price */}
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Wifi className="h-4 w-4 text-green-400" />
                <span className="text-sm font-medium text-slate-300">
                  Bandwidth Unit Price
                </span>
              </div>
              <span className="text-xs text-slate-500">SUN per byte</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {isConnected ? formatNumber(bandwidthUnitSun) : '--'}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Cost per bandwidth byte in SUN
            </p>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
          <p className="text-xs text-blue-200">
            <strong>Live Data:</strong> These prices are fetched directly from
            the TRON network and update automatically.
          </p>
        </div>
      </div>

      {/* TRX Price Status */}
      <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-white flex items-center space-x-2">
            <DollarSign className="h-5 w-5 text-green-400" />
            <span>TRX Price (Live)</span>
          </h3>
          <button
            onClick={fetchTrxPrice}
            className="p-2 text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-lg transition-colors"
            title="Refresh TRX price"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Current Price */}
          <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-green-300">
                Current Price
              </span>
              <span className="text-xs text-green-400">USD</span>
            </div>
            <div className="text-3xl font-bold text-green-400">
              ${trxPrice?.toFixed(4) || '0.0000'}
            </div>
            <p className="text-xs text-green-200 mt-1">
              Live price from CoinGecko
            </p>
          </div>

          {/* Price Sources */}
          <div>
            <h4 className="font-medium mb-3 text-white">Price Sources</h4>
            <div className="space-y-2 text-sm text-slate-300">
              <div className="flex items-center justify-between">
                <span>Primary:</span>
                <span className="text-green-400">CoinGecko</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Fallback 1:</span>
                <span className="text-blue-400">Binance</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Fallback 2:</span>
                <span className="text-purple-400">Coinbase</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
          <p className="text-xs text-green-200">
            <strong>Auto-refresh:</strong> TRX price updates automatically every
            few minutes to ensure accurate calculations.
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="card p-6 bg-white/5 backdrop-blur-sm border border-slate-700/50">
        <h3 className="font-semibold mb-4 text-white flex items-center space-x-2">
          <Activity className="h-5 w-5 text-purple-400" />
          <span>Quick Stats</span>
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-300">Last Updated:</span>
            <span className="text-sm text-slate-400">
              {new Date().toLocaleTimeString()}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-300">Network:</span>
            <span className="text-sm text-green-400">TRON Mainnet</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-300">API Endpoint:</span>
            <span className="text-sm text-slate-400">api.trongrid.io</span>
          </div>
        </div>

        <div className="mt-4 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
          <p className="text-xs text-purple-200">
            <strong>Pro Tip:</strong> These live prices ensure your staking
            calculations are always accurate and up-to-date.
          </p>
        </div>
      </div>
    </div>
  );
}
